import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp, Activity } from "lucide-react";
import { Card } from "./ui/card";
import api from "../lib/api";

// GAF score buckets (DSM-IV-TR style)
const ZONES = [
  { min: 91, max: 100, label: "極めて良好", color: "#3A6B52" },
  { min: 81, max: 90, label: "良好", color: "#5D8B6E" },
  { min: 71, max: 80, label: "症状軽度", color: "#8FAD7A" },
  { min: 61, max: 70, label: "軽度", color: "#C6B85F" },
  { min: 51, max: 60, label: "中等度", color: "#D4A373" },
  { min: 41, max: 50, label: "重度", color: "#C97F4F" },
  { min: 31, max: 40, label: "重大", color: "#A74141" },
  { min: 1, max: 30, label: "危険", color: "#7A2A2A" },
];

const labelFor = (score) => {
  if (!score && score !== 0) return "";
  const z = ZONES.find((z) => score >= z.min && score <= z.max);
  return z ? z.label : "";
};

const colorFor = (score) => {
  const z = ZONES.find((z) => score >= z.min && score <= z.max);
  return z ? z.color : "#8A8A86";
};

export default function GafTrendChart({ patientId }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    api
      .get("/records", { params: { patient_id: patientId } })
      .then((r) => {
        const data = r.data || [];
        const filtered = data
          .filter((rec) => rec.gaf_score != null && rec.record_date)
          .map((rec) => ({
            date: rec.record_date,
            score: Number(rec.gaf_score),
            label: labelFor(Number(rec.gaf_score)),
          }))
          .sort((a, b) => a.date.localeCompare(b.date));
        setPoints(filtered);
      })
      .finally(() => setLoading(false));
  }, [patientId]);

  const latest = points[points.length - 1];
  const previous = points.length > 1 ? points[points.length - 2] : null;
  const delta = latest && previous ? latest.score - previous.score : null;

  return (
    <Card className="p-5 border-[#E5E5E3]" data-testid="gaf-trend-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2C5545]/10 flex items-center justify-center">
            <Activity size={18} className="text-[#2C5545]" />
          </div>
          <div>
            <h3 className="font-bold text-[#1C1C1A]">GAF スコア推移</h3>
            <p className="text-xs text-[#8A8A86]">看護記録から自動集計</p>
          </div>
        </div>
        {latest && (
          <div className="flex items-center gap-3" data-testid="gaf-latest">
            <div className="text-right">
              <div className="text-[10px] text-[#8A8A86] font-numeric tracking-wider">直近スコア</div>
              <div className="flex items-center gap-1 justify-end">
                <span className="text-2xl font-bold font-numeric" style={{ color: colorFor(latest.score) }}>{latest.score}</span>
                {delta !== null && (
                  <span
                    className={`text-xs font-medium font-numeric ${delta > 0 ? "text-[#3A6B52]" : delta < 0 ? "text-[#A74141]" : "text-[#8A8A86]"}`}
                  >
                    {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"}{Math.abs(delta) || ""}
                  </span>
                )}
              </div>
            </div>
            <div
              className="text-[10px] font-medium px-2 py-1 rounded-full"
              style={{ background: colorFor(latest.score) + "22", color: colorFor(latest.score) }}
            >
              {latest.label}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-[#8A8A86] text-sm">読み込み中...</div>
      ) : points.length === 0 ? (
        <div className="h-48 flex flex-col items-center justify-center text-[#8A8A86] text-sm gap-2">
          <TrendingUp size={28} className="opacity-30" />
          <div>看護記録に GAF スコアが登録されると、ここに推移グラフが表示されます。</div>
        </div>
      ) : (
        <div className="w-full h-56" data-testid="gaf-chart-svg">
          <ResponsiveContainer>
            <LineChart data={points} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E5E5E3" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8A86" }} />
              <YAxis domain={[0, 100]} ticks={[0, 30, 50, 70, 90, 100]} tick={{ fontSize: 10, fill: "#8A8A86" }} />
              <ReferenceLine y={50} stroke="#D4A373" strokeDasharray="3 3" label={{ value: "50", fontSize: 9, fill: "#D4A373" }} />
              <ReferenceLine y={70} stroke="#8FAD7A" strokeDasharray="3 3" label={{ value: "70", fontSize: 9, fill: "#8FAD7A" }} />
              <Tooltip
                contentStyle={{ background: "white", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }}
                formatter={(value) => [`${value} (${labelFor(value)})`, "GAF"]}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#2C5545"
                strokeWidth={2.5}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  return <circle cx={cx} cy={cy} r={5} fill={colorFor(payload.score)} stroke="white" strokeWidth={2} />;
                }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {points.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-[#E5E5E3]">
          <div data-testid="gaf-stat-records">
            <div className="text-[10px] text-[#8A8A86] font-numeric tracking-wider">記録数</div>
            <div className="text-base font-bold font-numeric">{points.length}</div>
          </div>
          <div>
            <div className="text-[10px] text-[#8A8A86] font-numeric tracking-wider">最高</div>
            <div className="text-base font-bold font-numeric text-[#3A6B52]">
              {Math.max(...points.map((p) => p.score))}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[#8A8A86] font-numeric tracking-wider">最低</div>
            <div className="text-base font-bold font-numeric text-[#A74141]">
              {Math.min(...points.map((p) => p.score))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
